import * as React from "react";
import {observer} from "mobx-react";
import {withAppStores} from "../../../../../AppStores";
import type {Field} from "../../../../../data/models/field/Field";
import {XyiconFeature} from "../../../../../generated/api/base";
import type {AppState} from "../../../../../data/state/AppState";
import {featureTitles} from "../../../../../data/state/AppStateConstants";
import {FieldDataTypes} from "../../../../../data/models/field/FieldDataTypes";
import type {IHeader} from "../../../../widgets/table/SimpleTable";
import {SettingTableTabV5} from "../abstract/SettingTableTabV5";
import {FieldDetailsContentV5} from "./FieldDetailsContentV5";
import {CreateFieldPanelV5} from "./CreateFieldPanelV5";

interface IFieldSettingsProps {
	readonly feature: XyiconFeature;
	readonly typesFeature: XyiconFeature;
	readonly appState?: AppState;
}

interface IFieldSettingsState {
	headers: IHeader[];
}

@withAppStores("appState")
@observer
export class FieldSettingsV5 extends React.Component<IFieldSettingsProps, IFieldSettingsState> {
	private _headers: IHeader[] = [];

	private _appState: AppState;

	constructor(props: IFieldSettingsProps) {
		super(props);
		this._appState = props.appState;

		this._headers = [
			{
				id: "refId",
				title: "ID",
			},
			{
				id: "name",
				title: "Name",
			},
			{
				id: "type",
				title: "Type",
			},
			{
				id: "displayOnLinks",
				title: "Display On Links",
			},
			[XyiconFeature.Boundary, XyiconFeature.Xyicon].includes(props.feature) && {
				id: "hasFormula",
				title: "Has Formula",
			},
			props.appState.actions.isFieldValidationRuleAvailableInOrganization(props.feature) && {
				id: "validation",
				title: "Validation Rule",
			},
			{
				id: "helperText",
				title: "Helper Text",
			},
			{
				id: "description",
				title: "Field Description",
			},
		].filter((i) => !!i);
	}

	private getFields = (item: Field, index: number) => {
		return this._headers.map((header) => {
			// TODO
			const id = header.id;

			switch (id) {
				case "displayOnLinks":
					return this._appState.actions.getBooleanFieldTitle(item.displayOnLinks);
				case "hasFormula":
					return this._appState.actions.getBooleanFieldTitle(item.hasFormula);
				case "type":
					const dataType = FieldDataTypes.map[item.dataType];

					if (dataType) {
						return dataType.title;
					}
					break;
				case "validation":
					return item.validation;
				case "description":
					return item.fieldDescription;
				default:
					return item[id as "id" | "name"];
			}
		});
	};

	public override componentDidUpdate(prevProps: IFieldSettingsProps) {
		if (prevProps.appState !== this.props.appState) {
			this._appState = this.props.appState;
		}
	}

	public override render() {
		const {feature, typesFeature} = this.props;

		return (
			<SettingTableTabV5
				headers={this._headers}
				getFields={this.getFields}
				feature={feature}
				typesFeature={typesFeature}
				kind="fields"
				create={(onCancel, onCreated) => (
					<CreateFieldPanelV5
						feature={feature}
						typesFeature={typesFeature}
						onCancel={onCancel}
						onCreated={onCreated}
					/>
				)}
				right={(selected) => (
					<FieldDetailsContentV5
						selected={selected}
						typesFeature={typesFeature}
					/>
				)}
				emptyListText={`There are no ${featureTitles[feature]} Fields to list.`}
			/>
		);
	}
}
